import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, increment, writeBatch, serverTimestamp, runTransaction } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { PDFDocument, StandardFonts } from 'pdf-lib';
import { ArrowLeft, Download, Share, Copy, Check, DollarSign, FileText, ExternalLink, ChevronDown, ChevronUp, Link as LinkIcon, ChevronRight, Info } from 'lucide-react';
import { Toaster, toast } from 'react-hot-toast';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../utils/firebase';

const REPORT_FIELD_FONT_SIZE = 12;
const REPORT_FIELD_PADDING = 1;
const TEMPLATE_LOAD_OPTIONS = { ignoreEncryption: true, updateMetadata: false };

const lastIndexOfWhitespace = (line) => {
  for (let idx = line.length; idx > 0; idx--) {
    if (/\s/.test(line[idx])) return idx;
  }
  return undefined;
};

const splitOutLine = (input, maxWidth, font, fontSize) => {
  let lastWhitespaceIdx = input.length;
  while (lastWhitespaceIdx > 0) {
    const line = input.substring(0, lastWhitespaceIdx);
    const width = font.widthOfTextAtSize(line, fontSize);
    if (width < maxWidth) {
      const remainder = input.substring(lastWhitespaceIdx) || undefined;
      return { line, remainder };
    }
    lastWhitespaceIdx = lastIndexOfWhitespace(line) ?? 0;
  }
  return { line: input, remainder: undefined };
};

const wrapLikePdfLib = (text, font, fontSize, maxWidth) => {
  const cleaned = String(text || '')
    .replace(/\t|\u0085|\u2028|\u2029/g, '    ')
    .replace(/[\b\v]/g, '');
  const paragraphs = cleaned.split(/[\n\f\r\u000B]/);
  const lines = [];
  for (const para of paragraphs) {
    if (para === '') {
      lines.push('');
      continue;
    }
    let remaining = para;
    while (remaining !== undefined) {
      const { line, remainder } = splitOutLine(remaining, maxWidth, font, fontSize);
      lines.push(line);
      remaining = remainder?.trim();
    }
  }
  return lines;
};

const paginateReportFieldText = (text, font, fontSize, rect) => {
  const boundsWidth = rect.width - REPORT_FIELD_PADDING * 2;
  const boundsHeight = rect.height - REPORT_FIELD_PADDING * 2;
  const glyphHeight = font.heightAtSize(fontSize);
  const lineHeight = glyphHeight + glyphHeight * 0.2;
  const linesPerPage = Math.max(1, Math.floor((boundsHeight - lineHeight * 0.35) / lineHeight));
  const lines = wrapLikePdfLib(text, font, fontSize, boundsWidth);

  if (lines.length <= linesPerPage) return [text || ''];

  const chunks = [];
  for (let i = 0; i < lines.length; i += linesPerPage) {
    chunks.push(lines.slice(i, i + linesPerPage).join('\n'));
  }
  return chunks;
};

const fillReportTemplate = (pdfDoc, { clientName, property, dateLong, dateShort, reportText, continued }) => {
  const form = pdfDoc.getForm();
  form.getTextField('headerName').setText(`Name: ${clientName}${continued ? ' (Continued)' : ''}`);
  form.getTextField('headerProperty').setText(`Subject Property: ${property}`);
  form.getTextField('headerDate').setText(dateLong);
  form.getTextField('date').setText(dateShort);
  form.getTextField('report').setText(reportText || '');
  form.flatten();
};

const ShareReport = ({ isPublicAccess = true }) => {
  const [user, loading] = useAuthState(auth);
  const { reportId } = useParams();
  const location = useLocation();
  const [isFromPortal, setIsFromPortal] = useState(false);
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shareableLink, setShareableLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Add removeUSA function
  const removeUSA = (addr) => {
    if (!addr) return '';
    return addr
      .replace(/,\s*USA$/i, '')
      .replace(/,\s*[A-Z]{2}\s*$/, '')
      .replace(/,\s*United States$/i, '')
      .replace(/,\s*New Jersey$/i, '')
      .replace(/,\s*NJ$/i, '');
  };

  // Check if user came from oiltankportal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const source = params.get('source');
    setIsFromPortal(source === 'portal');
  }, [location]);

  useEffect(() => {
    const loadReport = async () => {
      try {
        setIsLoading(true);
        if (reportId) {
          const reportRef = doc(db, 'inspections', reportId);
          const reportSnap = await getDoc(reportRef);
          
          if (reportSnap.exists()) {
            const rawData = reportSnap.data();
            
            // Ensure updatedAt is always a string for consistency
            if (rawData.updatedAt && typeof rawData.updatedAt !== 'string') {
              // If it has toDate method (Firebase Timestamp)
              if (rawData.updatedAt.toDate) {
                rawData.updatedAt = rawData.updatedAt.toDate().toISOString();
              }
            }
            
            // Initialize missing fields with defaults for safety
            const data = { 
              id: reportSnap.id, 
              ...rawData,
              isPaid: rawData.isPaid ?? true, // Default to true if missing
              viewCount: rawData.viewCount ?? 0,
              viewLog: rawData.viewLog ?? [],
              downloadCount: rawData.downloadCount ?? 0,
              downloadLog: rawData.downloadLog ?? [],
              imageUrls: rawData.imageUrls ?? []
            };
            
            setReportData(data);
            
            // Only proceed with view count logic if auth state is determined (not loading)
            if (!loading) {
              // Increment view count for anyone except the creator
              if (!data.createdBy || data.createdBy !== user?.uid) {
                try {
                  await runTransaction(db, async (transaction) => {
                    const reportDoc = await transaction.get(reportRef);
                    if (!reportDoc.exists()) {
                      throw new Error("Document does not exist!");
                    }
                    
                    const currentViewCount = reportDoc.data().viewCount || 0;
                    const currentViewLog = reportDoc.data().viewLog || [];
                    
                    // Add new view entry with timestamp
                    const newViewEntry = {
                      timestamp: new Date().toISOString(),
                      userId: user?.uid || 'anonymous',
                      userName: user?.displayName || 'Anonymous User',
                      userEmail: user?.email || 'Unknown'
                    };
                    
                    transaction.update(reportRef, {
                      viewCount: currentViewCount + 1,
                      viewLog: [...currentViewLog, newViewEntry]
                    });
                  });
                } catch (error) {
                  console.warn('View count update failed:', error);
                }
              }
            }
          } else {
            toast.error('Report not found');
            if (!isPublicAccess) {
              navigate('/');
            }
            return;
          }
        } else if (location.state?.reportData) {
          setReportData(location.state.reportData);
        } else {
          toast.error('No report data found');
          navigate('/');
          return;
        }
      } catch (error) {
        console.error('Error loading report:', error);
        toast.error('Error loading report');
        if (!isPublicAccess) {
          navigate('/');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadReport();
  }, [reportId, location.state, navigate, isPublicAccess, user, loading]);

  useEffect(() => {
    if (!reportData) return;

    // Generate shareable link if we have a report ID
    if (reportData.id) {
      const link = `${window.location.origin}/share-report/${reportData.id}`;
      setShareableLink(link);
    }

    const generatePDF = async () => {
      if (!reportData) return;
      
      try {
        setIsLoading(true);
        const existingPdfBytes = await fetch('/template2.pdf').then(res => res.arrayBuffer());
        const loadTemplateDoc = () => PDFDocument.load(existingPdfBytes, TEMPLATE_LOAD_OPTIONS);
        const pdfDoc = await loadTemplateDoc();
        const form = pdfDoc.getForm();

        // Helper function to format date
        const formatDate = (dateValue) => {
          if (!dateValue) return 'Date: N/A';
          try {
            let date;
            
            // Handle Firebase Timestamp objects that might be serialized in different ways
            if (typeof dateValue === 'object' && dateValue.seconds && dateValue.nanoseconds) {
              // This is a Firebase Timestamp object
              date = new Date(dateValue.seconds * 1000);
            } else if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
              // Plain calendar date: build it in local time so it never shifts to the day before
              const [y, m, d] = dateValue.split('-').map(Number);
              date = new Date(y, m - 1, d);
            } else if (typeof dateValue === 'string' && dateValue.includes('at')) {
              // This handles the case when it's a string like "March 4, 2025 at 1:37:30 AM UTC-5"
              date = new Date(dateValue);
            } else {
              // Handle normal date strings
              date = new Date(dateValue);
            }
            
            // Check if date is invalid
            if (isNaN(date.getTime())) {
              console.warn('Invalid date encountered:', dateValue);
              return 'Date: N/A';
            }
            
            const monthNames = ["January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ];
            return `Date: ${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
          } catch (error) {
            console.error('Error formatting date:', error, dateValue);
            return 'Date: N/A';
          }
        };

        const formatShortDate = (dateValue) => {
          if (!dateValue) return 'N/A';
          try {
            let date;
            
            // Handle Firebase Timestamp objects that might be serialized in different ways
            if (typeof dateValue === 'object' && dateValue.seconds && dateValue.nanoseconds) {
              // This is a Firebase Timestamp object
              date = new Date(dateValue.seconds * 1000);
            } else if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
              // Plain calendar date: build it in local time so it never shifts to the day before
              const [y, m, d] = dateValue.split('-').map(Number);
              date = new Date(y, m - 1, d);
            } else if (typeof dateValue === 'string' && dateValue.includes('at')) {
              // This handles the case when it's a string like "March 4, 2025 at 1:37:30 AM UTC-5"
              date = new Date(dateValue);
            } else {
              // Handle normal date strings
              date = new Date(dateValue);
            }
            
            // Check if date is invalid
            if (isNaN(date.getTime())) {
              console.warn('Invalid date encountered:', dateValue);
              return 'N/A';
            }
            
            return date.toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
            });
          } catch (error) {
            console.error('Error formatting short date:', error);
            return 'N/A';
          }
        };

        // Generate the complete report text (v1.7.12: inspection type, GPR scope, equipment, conclusions)
        let reportText = reportData.fullDocumentText || '';

        if (!reportText) {
          const loc = reportData.anomalyLocation === 'custom' ? (reportData.customLocation || '') : (reportData.anomalyLocation || '');
          const isGpr = reportData.inspectionType === 'gpr';
          const isTargeted = reportData.gprScope === 'targeted';

          // Intro by scenario
          if (reportData.scenario === 'noTank') {
            reportText = 'Smart Choice Inspections LLC conducted a visual inspection and physical sweep of the referenced property to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). We did not detect any anomalies with our metal detection equipment that match the size of a UST.';
          } else if (reportData.scenario === 'anomaly') {
            reportText = `Smart Choice Inspections LLC conducted a visual inspection and physical sweep of the referenced property to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). A metallic anomaly, similar in size to an underground storage tank (UST), was detected${loc ? ' ' + loc : ''}.`;
          } else if (reportData.scenario === 'ustFound') {
            reportText = `Smart Choice Inspections LLC conducted a visual inspection and physical sweep of the referenced property to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). An underground storage tank (UST) was identified${loc ? ' ' + loc : ''}.`;
          } else if (reportData.scenario === 'dataInconclusive') {
            reportText = 'Smart Choice Inspections LLC conducted a visual inspection and physical sweep of the referenced property to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). Site conditions prevented a definitive conclusion regarding the presence of an underground storage tank (UST).';
          } else {
            reportText = 'Smart Choice Inspections LLC conducted a visual inspection and physical sweep of the referenced property to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). We did not detect any anomalies with our metal detection equipment that match the size of a UST.';
          }

          // Conclusion (GPR full no UST: additional notes & limitations go before "While non-invasive...")
          if (reportData.scenario === 'noTank') {
            if (!isGpr) {
              if (reportData.customText?.trim()) {
                reportText += ' ' + reportData.customText.trim();
                if (!reportData.customText.trim().endsWith('.')) reportText += '.';
              }
              if (reportData.limitations?.length > 0) {
                const lt = generateLimitationsText(reportData.limitations);
                if (lt) reportText += ' ' + lt;
              }
              reportText += ' This does not guarantee the absence of USTs on the property; but it is unlikely.';
            } else if (isTargeted) {
              if (reportData.customText?.trim()) {
                reportText += ' ' + reportData.customText.trim();
                if (!reportData.customText.trim().endsWith('.')) reportText += '.';
              }
              if (reportData.limitations?.length > 0) {
                const lt = generateLimitationsText(reportData.limitations);
                if (lt) reportText += ' ' + lt;
              }
              reportText += ' The evaluated anomaly did not exhibit radar characteristics consistent with an underground storage tank. This conclusion applies only to the defined evaluation area.';
            } else {
              reportText += ' No metallic responses or hyperbolic radar reflections consistent with the size and cross-sectional shape of an underground storage tank were observed in the scanned areas.';
              if (reportData.customText?.trim()) {
                reportText += ' ' + reportData.customText.trim();
                if (!reportData.customText.trim().endsWith('.')) reportText += '.';
              }
              if (reportData.limitations?.length > 0) {
                const lt = generateLimitationsText(reportData.limitations);
                if (lt) reportText += ' ' + lt;
              }
              reportText += ' While non-invasive detection methods are effective, no method can guarantee the complete absence of buried objects.';
            }
          } else if (reportData.scenario === 'anomaly') {
            if (reportData.customText?.trim()) {
              reportText += ' ' + reportData.customText.trim();
              if (!reportData.customText.trim().endsWith('.')) reportText += '.';
            }
            if (reportData.limitations?.length > 0) {
              const lt = generateLimitationsText(reportData.limitations);
              if (lt) reportText += ' ' + lt;
            }
            reportText += ' We recommend requesting OPRA records for any information on previously decommissioned oil tanks. If no records are found, proceed with an intrusive investigation.';
          } else if (reportData.scenario === 'ustFound') {
            if (reportData.customText?.trim()) {
              reportText += ' ' + reportData.customText.trim();
              if (!reportData.customText.trim().endsWith('.')) reportText += '.';
            }
            if (reportData.limitations?.length > 0) {
              const lt = generateLimitationsText(reportData.limitations);
              if (lt) reportText += ' ' + lt;
            }
            if (isGpr && isTargeted) reportText += ' A distinct hyperbolic reflection consistent with the size and cross-sectional shape of an underground storage tank was observed within the defined evaluation area. Further intrusive verification is recommended.';
            else reportText += ' This UST may pose a risk of contamination if not addressed. We strongly recommend the removal or proper decommissioning of this UST, along with soil testing for potential contamination.';
          } else if (reportData.scenario === 'dataInconclusive') {
            if (reportData.customText?.trim()) {
              reportText += ' ' + reportData.customText.trim();
              if (!reportData.customText.trim().endsWith('.')) reportText += '.';
            }
            if (reportData.limitations?.length > 0) {
              const lt = generateLimitationsText(reportData.limitations);
              if (lt) reportText += ' ' + lt;
            }
            reportText += ' Follow-up evaluation may be necessary.';
          }

          // Equipment (targeted = only the defined area, no 30ft sweep; full = 30ft metal + optional full GPR)
          const getGprTargetedReasonText = (data) => {
            const r = data?.gprTargetedReason;
            if (r === 'metal_detection') return 'a metal detection anomaly';
            if (r === 'visual') return 'a visual indicator (fill or vent pipe)';
            if (r === 'prior_docs') return 'prior documentation';
            if (r === 'client_request') return 'client request';
            if (r === 'other') return (data?.gprTargetedReasonOther?.trim()) || 'other';
            return '';
          };
          const targetedLoc = reportData.gprTargetedLocation?.trim() || '';
          if (isTargeted && targetedLoc) {
            const reasonText = getGprTargetedReasonText(reportData);
            const reasonPhrase = reasonText ? ', due to ' + reasonText + ',' : '';
            reportText += '\n\nUtilizing electronic metal detection equipment (Fisher TW-6) and a magnetic locating device (Schonstedt 92XTD), the defined area of concern located at ' + targetedLoc + ' was evaluated for buried metallic objects consistent with underground storage tanks. Ground-penetrating radar (Easy Radar ER500A) was also utilized to evaluate that same area' + reasonPhrase + ' to assess subsurface conditions. This was a targeted evaluation only; the full property was not swept and this does not represent a full-property subsurface scan.';
          } else {
            const metalPara = 'Utilizing electronic metal detection equipment (Fisher TW-6) and a magnetic locating device (Schonstedt 92XTD), accessible yard areas were swept for buried metallic objects consistent with underground storage tanks. The detection equipment used can detect metallic objects up to a depth of six feet. The property was swept to a distance of at least 30ft around the perimeter of the house.';
            reportText += '\n\n' + metalPara;
            if (isGpr) {
              reportText += '\n\nIn addition, ground-penetrating radar (Easy Radar ER500A) was utilized to evaluate subsurface conditions and identify hyperbolic reflections consistent with underground storage tanks.';
            }
          }
          if (!isGpr && reportData.scenario === 'noTank') {
            reportText += '\n\nNo other metal anomalies consistent with the size of an underground storage tank were identified during the oil tank sweep.';
          }
          reportText += '\n\nIf you have any questions about this report please feel free to contact us.\n\nTerms and Conditions: This report excludes areas where access to the magnetic locator was restricted due to obstructions such as dense vegetation, denied access, or structural interference. The methods used at this site have been used successfully at many sites to locate underground storage tanks and drums. There can be no guarantee, however, that every target will be detected at a particular site. Subsurface conditions (e.g. rebar/wire mesh under concrete, foundations, structures and others) may prevent some or all geophysical locating methods from working properly.';
          if (isGpr) reportText += ' Ground-penetrating radar performance may be affected by soil composition, moisture content, reinforced concrete, buried utilities, subsurface debris, or signal attenuation. Radar reflections represent geophysical responses and may require intrusive confirmation.';
          if (isGpr && isTargeted) reportText += ' This evaluation was limited to the specific area identified above. Areas outside that defined zone were not assessed using radar imaging.';
        }

        const headerFields = {
          clientName: reportData.clientName || 'N/A',
          property: reportData.propertyAddress ? removeUSA(reportData.propertyAddress) : 'N/A',
          dateLong: formatDate(reportData.dateOfInspection),
          dateShort: formatShortDate(reportData.dateOfInspection)
        };

        const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const reportRect = form.getTextField('report').acroField.getWidgets()[0].getRectangle();
        const pageChunks = paginateReportFieldText(
          reportText,
          helvetica,
          REPORT_FIELD_FONT_SIZE,
          reportRect
        );

        fillReportTemplate(pdfDoc, { ...headerFields, reportText: pageChunks[0] || '', continued: false });

        let outputDoc = pdfDoc;
        if (pageChunks.length > 1) {
          outputDoc = await PDFDocument.create();
          const [firstPage] = await outputDoc.copyPages(pdfDoc, [0]);
          outputDoc.addPage(firstPage);

          for (let pageIndex = 1; pageIndex < pageChunks.length; pageIndex++) {
            const continuedDoc = await loadTemplateDoc();
            fillReportTemplate(continuedDoc, {
              ...headerFields,
              reportText: pageChunks[pageIndex],
              continued: true
            });
            const [continuedPage] = await outputDoc.copyPages(continuedDoc, [0]);
            outputDoc.addPage(continuedPage);
          }
        }

        // If there are images, add them to the PDF
        if (reportData.imageUrls?.length > 0) {
          try {
            for (let i = 0; i < reportData.imageUrls.length; i += 2) {
              const page = outputDoc.addPage();
              const { width, height } = page.getSize();
              const imageMargin = 50;
              const verticalSpacing = 20;
              const maxImageHeight = (height - (imageMargin * 2) - verticalSpacing) / 2;

              try {
                // Process first image
                const image1Response = await fetch(reportData.imageUrls[i].url);
                if (!image1Response.ok) {
                  console.error(`Failed to fetch image ${i}: ${image1Response.status}`);
                  continue; // Skip this page and try the next pair
                }
                
                const image1ArrayBuffer = await image1Response.arrayBuffer();
                const image1Format = reportData.imageUrls[i].url.toLowerCase().endsWith('.png') ? 'png' : 'jpeg';
                const embeddedImage1 = image1Format === 'png' 
                  ? await outputDoc.embedPng(image1ArrayBuffer)
                  : await outputDoc.embedJpg(image1ArrayBuffer);

                // Calculate dimensions for first image
                const img1Dims = fitImageToPage(
                  embeddedImage1.width,
                  embeddedImage1.height,
                  width - (imageMargin * 2),
                  maxImageHeight
                );

                // Draw first image
                page.drawImage(embeddedImage1, {
                  x: (width - img1Dims.width) / 2,
                  y: height - imageMargin - img1Dims.height,
                  width: img1Dims.width,
                  height: img1Dims.height
                });

                // If there's a second image
                if (i + 1 < reportData.imageUrls.length) {
                  try {
                    const image2Response = await fetch(reportData.imageUrls[i + 1].url);
                    if (!image2Response.ok) {
                      console.error(`Failed to fetch image ${i+1}: ${image2Response.status}`);
                      continue; // Skip just the second image
                    }
                    
                    const image2ArrayBuffer = await image2Response.arrayBuffer();
                    const image2Format = reportData.imageUrls[i + 1].url.toLowerCase().endsWith('.png') ? 'png' : 'jpeg';
                    const embeddedImage2 = image2Format === 'png'
                      ? await outputDoc.embedPng(image2ArrayBuffer)
                      : await outputDoc.embedJpg(image2ArrayBuffer);

                    // Calculate dimensions for second image
                    const img2Dims = fitImageToPage(
                      embeddedImage2.width,
                      embeddedImage2.height,
                      width - (imageMargin * 2),
                      maxImageHeight
                    );

                    // Draw second image
                    page.drawImage(embeddedImage2, {
                      x: (width - img2Dims.width) / 2,
                      y: imageMargin,
                      width: img2Dims.width,
                      height: img2Dims.height
                    });
                  } catch (error) {
                    console.error(`Error processing second image (${i+1}):`, error);
                    // Continue with the PDF without this image
                  }
                }
              } catch (error) {
                console.error(`Error processing image ${i}:`, error);
                // Continue with the next page
              }
            }
          } catch (error) {
            console.error('Error processing images:', error);
            // Continue generating the PDF without images
          }
        }

        // Save the PDF and convert it to a blob URL
        const pdfBytes = await outputDoc.save({
          useObjectStreams: false,
          addDefaultPage: false,
          updateMetadata: false
        });
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (error) {
        console.error('Error generating PDF:', error);
        toast.error('Error generating PDF');
      } finally {
        setIsLoading(false);
      }
    };

    // Only generate PDF when needed
    if (reportData) {
      generatePDF();
    }

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [reportData]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to fit image onto a PDF page
  const fitImageToPage = (imgWidth, imgHeight, maxWidth, maxHeight) => {
    const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
    return {
      width: imgWidth * ratio,
      height: imgHeight * ratio
    };
  };

  // Report title by inspection type and scope (v1.7.12)
  const getReportTitle = (data) => {
    if (!data) return 'Oil Tank Sweep Report';
    const type = data.inspectionType || 'standard';
    const scope = data.gprScope || 'full';
    if (type === 'standard') return 'Oil Tank Sweep Report';
    if (type === 'gpr' && scope === 'targeted') return 'Oil Tank Sweep Report with Targeted Ground-Penetrating Radar Evaluation';
    if (type === 'gpr') return 'Oil Tank Sweep Report with Ground-Penetrating Radar Imaging';
    return 'Oil Tank Sweep Report';
  };

  // Helper function for limitations text
  const generateLimitationsText = (limitations) => {
    if (!limitations?.length) return '';

    const limitationStrings = limitations.map(limitation => {
      const type = limitation.type === 'custom' ? limitation.customType : limitation.type;
      const location = limitation.location === 'custom' ? limitation.customLocation : limitation.location;
      return `${type} ${location}`;
    });

    if (limitationStrings.length === 1) {
      return `However, ${limitationStrings[0]} obstructed our ability to sweep this area.`;
    }

    const lastLimitation = limitationStrings.pop();
    return `However, ${limitationStrings.join(', ')}, and ${lastLimitation} obstructed our ability to sweep these areas.`;
  };

  const handleDownload = async () => {
    if (!pdfUrl || !reportData) return;
    
    // Prevent download for unpaid reports for non-logged in users
    if (!reportData.isPaid && !user) {
      toast.error('Payment required to download this report');
      return;
    }
    
    // Show a loading indicator while tracking the download
    toast.loading('Preparing download...');
    
    try {
      // Track the download first to ensure it's counted
      // Only track downloads if the user is not the creator
      if (!reportData.createdBy || reportData.createdBy !== user?.uid) {
        const reportRef = doc(db, 'inspections', reportData.id);
        
        // Create a download entry with timestamp
        const newDownloadEntry = {
          timestamp: new Date().toISOString(),
          userId: user?.uid || 'anonymous',
          platform: isMobile ? 'mobile' : 'desktop'
        };
        
        // Use a transaction to update both count and log
        await runTransaction(db, async (transaction) => {
          const reportDoc = await transaction.get(reportRef);
          if (!reportDoc.exists()) {
            throw new Error("Document does not exist!");
          }
          
          const currentDownloadCount = reportDoc.data().downloadCount || 0;
          const currentDownloadLog = reportDoc.data().downloadLog || [];
          
          transaction.update(reportRef, {
            downloadCount: currentDownloadCount + 1,
            downloadLog: [...currentDownloadLog, newDownloadEntry]
          });
        });
        
        console.log('Download tracked successfully');
      }
      
      // Dismiss the loading toast
      toast.dismiss();
      toast.success('Download started');
      
      // Now trigger the download after tracking is complete
      const propertyName = reportData.propertyAddress ? reportData.propertyAddress.split(',')[0] : 'Property';
      const baseTitle = getReportTitle(reportData).replace(/\s+/g, ' ').trim();
      const fileName = `${baseTitle} - ${reportData.clientName || 'Client'} - ${propertyName}.pdf`;

      if (isMobile) {
        // For mobile devices, especially iOS, open in a new tab
        setTimeout(() => {
          window.open(pdfUrl, '_blank');
        }, 300); // Small delay to ensure toast is shown and tracking is complete
      } else {
        // For desktop browsers, use the download attribute
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error tracking download:', error);
      toast.dismiss();
      toast.error('Error tracking download');
      
      // Still allow the download even if tracking failed
      if (isMobile) {
        window.open(pdfUrl, '_blank');
      } else {
        const propertyName = reportData.propertyAddress ? reportData.propertyAddress.split(',')[0] : 'Property';
        const baseTitle = getReportTitle(reportData).replace(/\s+/g, ' ').trim();
        const fileName = `${baseTitle} - ${reportData.clientName || 'Client'} - ${propertyName}.pdf`;
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const handleShare = async () => {
    if (!shareableLink || !reportData) return;

    try {
      if (navigator.share) {
        const address = reportData.propertyAddress ? removeUSA(reportData.propertyAddress.split(',')[0]) : 'Property';
        const clientName = reportData.clientName || 'Client';
        const inspectionDate = reportData.dateOfInspection || new Date().toLocaleDateString();
        const fullAddress = reportData.propertyAddress ? removeUSA(reportData.propertyAddress) : 'the property';
        
        const reportTitle = getReportTitle(reportData);
        await navigator.share({
          title: `${reportTitle} - ${address}`,
          text: `${reportTitle} - ${address}\n\n-\n\nDear ${clientName},\n\nPlease find attached the ${reportTitle} for the property at ${fullAddress}. This report was conducted on ${inspectionDate} by Smart Choice Inspections LLC.\n\nYou can view the full report at the link below:\n`,
          url: shareableLink
        });
      } else {
        await navigator.clipboard.writeText(shareableLink);
        setCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        toast.error('Error sharing report');
      }
    }
  };

  const handleCopyLink = async () => {
    if (!shareableLink) return;

    try {
      await navigator.clipboard.writeText(shareableLink);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying link:', error);
      toast.error('Error copying link');
    }
  };

  const handleOpenInNewTab = async () => {
    // Prevent opening PDF in new tab for unpaid reports for non-logged in users
    if (!reportData.isPaid && !user) {
      toast.error('Payment required to view this report');
      return;
    }
    
    // Show a loading indicator
    toast.loading('Opening PDF...');
    
    try {
      // Track the download if the user is not the creator
      if (!reportData.createdBy || reportData.createdBy !== user?.uid) {
        const reportRef = doc(db, 'inspections', reportData.id);
        
        // Create a download entry with timestamp
        const newDownloadEntry = {
          timestamp: new Date().toISOString(),
          userId: user?.uid || 'anonymous',
          platform: isMobile ? 'mobile' : 'desktop'
        };
        
        // Use a transaction to update download count and log
        await runTransaction(db, async (transaction) => {
          const reportDoc = await transaction.get(reportRef);
          if (!reportDoc.exists()) {
            throw new Error("Document does not exist!");
          }
          
          const currentDownloadCount = reportDoc.data().downloadCount || 0;
          const currentDownloadLog = reportDoc.data().downloadLog || [];
          
          transaction.update(reportRef, {
            downloadCount: currentDownloadCount + 1,
            downloadLog: [...currentDownloadLog, newDownloadEntry]
          });
        });
        
        console.log('Download tracked successfully');
      }
      
      // Dismiss the loading toast
      toast.dismiss();
      
      // Open PDF in new tab after tracking is complete
      setTimeout(() => {
        toast.success('PDF opened in new tab');
        window.open(pdfUrl, '_blank');
      }, 300); // Small delay to ensure tracking is complete
    } catch (error) {
      console.error('Error tracking download:', error);
      toast.dismiss();
      
      // Still open the PDF even if tracking failed
      window.open(pdfUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // If reportData is still null after loading is complete, show an error message
  if (!reportData && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 max-w-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                Error: Could not load the report. The report may have been deleted or you may not have permission to view it.
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Sticky header */}
      <header className="sticky top-0 z-50 bg-white border-b">
        {user && (
          <div className="flex items-center p-4 border-b">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </button>
          </div>
        )}
        {!user && isFromPortal && (
          <div className="flex items-center p-4 border-b">
            <button
              onClick={() => window.open('https://portal.oiltank.app', '_self')}
              className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Portal
            </button>
          </div>
        )}
        <div className="p-2 sm:p-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                {reportData?.clientName} - {removeUSA(reportData?.propertyAddress)}
              </h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleDownload}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-w-[100px]"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </button>
                <button
                  onClick={handleShare}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-w-[100px]"
                >
                  <Share className="h-4 w-4 mr-2" />
                  Share
                </button>
                <button
                  onClick={handleCopyLink}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-w-[100px]"
                >
                  {copied ? (
                    <Check className="h-4 w-4 mr-2 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  Copy Link
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Scrollable PDF container */}
      <main className="flex-1 relative bg-gray-50 overflow-y-auto pb-24">
        {isMobile ? (
          <div className="flex flex-col items-center justify-center p-4 pb-24">
            {/* Note about additional documents on mobile */}
            {reportData?.resources && reportData.resources.length > 0 && !user && !isFromPortal && (
              <div className="mb-3 w-full bg-blue-50 rounded-lg p-2 text-center">
                <p className="text-sm text-blue-700">
                  <Info className="h-4 w-4 inline-block mr-1" />
                  View report and additional documents below
                </p>
              </div>
            )}
            
            {/* PDF open in new tab area */}
            <div className="bg-white rounded-lg shadow-sm w-full p-6 mb-6 text-center">
              <p className="text-gray-600 text-center mb-4">
                For the best viewing experience on mobile, please open the report in a new tab
              </p>
              <button
                onClick={handleOpenInNewTab}
                className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Open Report in New Tab
              </button>
              
              {/* Add refresh option for mobile - positioned closer to the button */}
              <div className="flex items-center justify-center mt-3 text-center">
                <span className="text-gray-500 text-xs">Trouble viewing this page?</span>
                <button 
                  onClick={() => window.location.reload()} 
                  className="ml-2 text-xs text-blue-500 hover:text-blue-700 font-medium"
                  title="Refreshing may solve cache-related issues"
                >
                  Refresh Page
                </button>
              </div>
            </div>
            
            {/* Additional Resources Section - Only show if report has resources (Mobile) */}
            {reportData?.resources && reportData.resources.length > 0 && (
              <div className="w-full">
                {/* Divider between report and resources */}
                <div className="my-6 flex items-center justify-center">
                  <div className="border-t border-gray-300 flex-grow"></div>
                  <span className="px-4 text-sm text-gray-500 font-medium">ADDITIONAL DOCUMENTS</span>
                  <div className="border-t border-gray-300 flex-grow"></div>
                </div>
                
                <div className="mb-6 w-full">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="text-md font-semibold text-blue-800 mb-3 flex items-center">
                      <FileText className="h-5 w-5 mr-2" />
                      Additional Documents & Resources
                    </h4>
                    
                    {/* Resources List */}
                    <ul className="space-y-2">
                      {reportData.resources.map((resource, index) => (
                        <li 
                          key={index} 
                          className="flex items-center p-2 bg-white rounded border border-gray-200"
                        >
                          {resource.type === 'file' ? (
                            <FileText className="h-4 w-4 text-blue-500 mr-3 flex-shrink-0" />
                          ) : (
                            <LinkIcon className="h-4 w-4 text-green-500 mr-3 flex-shrink-0" />
                          )}
                          <div className="flex-grow overflow-hidden">
                            <p className="text-sm font-medium text-gray-800 truncate">{resource.title}</p>
                          </div>
                          
                          {/* Show View button for logged-in users or if from portal */}
                          {(user || isFromPortal) ? (
                            <a 
                              href={resource.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="ml-3 px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors duration-150 flex items-center"
                            >
                              View <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          ) : (
                            <span 
                              className="ml-3 px-4 py-1 text-xs bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full flex items-center cursor-pointer hover:from-blue-600 hover:to-blue-700 transition-colors"
                              title="Create an account to access this document"
                              onClick={() => window.open(`https://portal.oiltank.app/register?reportId=${reportId}`, '_blank')}
                            >
                              Create Account <ChevronRight className="h-3 w-3 ml-1" />
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    
                    {/* Account Creation Notice - only for non-logged in users and not from portal */}
                    {!user && !isFromPortal && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-blue-50 border border-blue-200 rounded-md shadow-sm">
                        <h5 className="text-sm font-bold text-blue-800 mb-2 flex items-center">
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Access These Documents
                        </h5>
                        <p className="text-sm text-gray-700 mb-3">
                          Create a free account to access:
                        </p>
                        <ul className="text-sm text-gray-700 mb-3 space-y-1 pl-5 list-disc">
                          <li>Home Inspection Reports</li>
                          <li>NPMA-33 Wood Destroying Insect Reports</li>
                          <li>Sewer Scope Videos (if this service was performed)</li>
                          <li>Radon Test Results (if testing was conducted)</li>
                        </ul>
                        <p className="text-sm text-gray-700 mb-2">
                          <em>Documents vary by property and services performed.</em>
                        </p>
                        <div className="mt-3">
                          <a 
                            href={`https://portal.oiltank.app/register?reportId=${reportId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                          >
                            Create Your Free Account <ChevronRight className="h-4 w-4 ml-1" />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="container max-w-4xl mx-auto pb-24">
            {/* Centered help message between header and PDF */}
            <div className="flex justify-center items-center py-2">
              <span className="text-gray-500 text-xs">Having trouble viewing this report?</span>
              <button 
                onClick={() => window.location.reload()} 
                className="ml-2 text-xs text-blue-500 hover:text-blue-700 font-medium"
                title="Refreshing may solve cache-related issues"
              >
                Refresh Page
              </button>
            </div>
            
            {/* Main content container */}
            <div className="mt-1">
              {/* Show loading state while reportData is null */}
              {!reportData ? (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden relative min-h-[842px] flex flex-col items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  <p className="mt-4 text-gray-600">Loading report data...</p>
                </div>
              ) : 
                /* For unpaid reports viewed by non-logged in users, show only payment required message */
                (!reportData.isPaid && !user) ? (
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden relative min-h-[842px] flex flex-col items-center justify-center p-8">
                    <div className="p-8 bg-amber-50 rounded-lg border-2 border-amber-500 shadow-lg text-center max-w-md">
                      <div className="flex items-center justify-center mb-4">
                        <DollarSign className="h-12 w-12 text-amber-600 mr-2" />
                        <span className="text-2xl font-bold text-amber-800">PAYMENT REQUIRED</span>
                      </div>
                      <p className="text-lg text-gray-700 mb-4">
                        This report is pending payment and cannot be viewed.
                      </p>
                      <p className="text-md text-gray-600 mb-6">
                        Please contact the inspector to arrange payment and gain full access to this report.
                      </p>
                      <div className="text-sm text-gray-500 mt-6">
                        <p>Property: {reportData.propertyAddress ? removeUSA(reportData.propertyAddress) : ''}</p>
                        <p>Client: {reportData.clientName}</p>
                        <p>Date: {reportData.dateOfInspection ? new Date(reportData.dateOfInspection).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Note about additional documents - show only when resources exist and user isn't logged in */}
                    {reportData?.resources && reportData.resources.length > 0 && !user && !isFromPortal && (
                      <div className="mb-3 bg-blue-50 rounded-lg p-2 text-center">
                        <p className="text-sm text-blue-700">
                          <Info className="h-4 w-4 inline-block mr-1" />
                          Oil Tank Sweep Report with additional documents available below
                        </p>
                      </div>
                    )}
                    
                    {/* PDF Report - MOVED TO TOP */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
                      <iframe
                        src={pdfUrl + '#toolbar=0&navpanes=0&view=FitH'}
                        className="w-full"
                        style={{ 
                          border: 'none',
                          height: 'calc(100vh - 200px)',
                          minHeight: '842px', // A4 height in pixels
                          width: '100%',
                          overflow: 'auto',
                          WebkitOverflowScrolling: 'touch'
                        }}
                        title="PDF Report"
                      />
                    </div>
                    
                    {/* Additional Resources Section - MOVED TO BOTTOM - Only show if report has resources */}
                    {reportData?.resources && reportData.resources.length > 0 && (
                      <>
                        {/* Divider between report and resources */}
                        <div className="my-6 flex items-center justify-center">
                          <div className="border-t border-gray-300 flex-grow"></div>
                          <span className="px-4 text-sm text-gray-500 font-medium">ADDITIONAL DOCUMENTS</span>
                          <div className="border-t border-gray-300 flex-grow"></div>
                        </div>
                        
                        <div className="mb-6 max-w-4xl mx-auto">
                          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <h4 className="text-md font-semibold text-blue-800 mb-3 flex items-center">
                              <FileText className="h-5 w-5 mr-2" />
                              Additional Documents & Resources
                            </h4>
                            
                            {/* Resources List */}
                            <ul className="space-y-2">
                              {reportData.resources.map((resource, index) => (
                                <li 
                                  key={index} 
                                  className="flex items-center p-2 bg-white rounded border border-gray-200"
                                >
                                  {resource.type === 'file' ? (
                                    <FileText className="h-4 w-4 text-blue-500 mr-3 flex-shrink-0" />
                                  ) : (
                                    <LinkIcon className="h-4 w-4 text-green-500 mr-3 flex-shrink-0" />
                                  )}
                                  <div className="flex-grow overflow-hidden">
                                    <p className="text-sm font-medium text-gray-800 truncate">{resource.title}</p>
                                  </div>
                                  
                                  {/* Show View button for logged-in users or if from portal */}
                                  {(user || isFromPortal) ? (
                                    <a 
                                      href={resource.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="ml-3 px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors duration-150 flex items-center"
                                    >
                                      View <ExternalLink className="h-3 w-3 ml-1" />
                                    </a>
                                  ) : (
                                    <span 
                                      className="ml-3 px-4 py-1 text-xs bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full flex items-center cursor-pointer hover:from-blue-600 hover:to-blue-700 transition-colors"
                                      title="Create an account to access this document"
                                      onClick={() => window.open(`https://portal.oiltank.app/register?reportId=${reportId}`, '_blank')}
                                    >
                                      Create Account <ChevronRight className="h-3 w-3 ml-1" />
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                            
                            {/* Account Creation Notice - only for non-logged in users and not from portal */}
                            {!user && !isFromPortal && (
                              <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-blue-50 border border-blue-200 rounded-md shadow-sm">
                                <h5 className="text-sm font-bold text-blue-800 mb-2 flex items-center">
                                  <ExternalLink className="h-4 w-4 mr-1" />
                                  Access These Documents
                                </h5>
                                <p className="text-sm text-gray-700 mb-3">
                                  Create a free account to access:
                                </p>
                                <ul className="text-sm text-gray-700 mb-3 space-y-1 pl-5 list-disc">
                                  <li>Home Inspection Reports</li>
                                  <li>NPMA-33 Wood Destroying Insect Reports</li>
                                  <li>Sewer Scope Videos (if this service was performed)</li>
                                  <li>Radon Test Results (if testing was conducted)</li>
                                </ul>
                                <p className="text-sm text-gray-700 mb-2">
                                  <em>Documents vary by property and services performed.</em>
                                </p>
                                <div className="mt-3">
                                  <a 
                                    href={`https://portal.oiltank.app/register?reportId=${reportId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                                  >
                                    Create Your Free Account <ChevronRight className="h-4 w-4 ml-1" />
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
            </div>
          </div>
        )}
      </main>

      {/* Promotional banner for client portal - shown only to non-logged in users and when not from portal */}
      {!user && !isFromPortal && reportData && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-3 px-4 shadow-lg">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between">
            <div className="mb-3 sm:mb-0 text-center sm:text-left">
              <h3 className="font-bold text-lg">Access your reports anytime!</h3>
              <p className="text-sm text-blue-100">Save this report and view all property documents in one place.</p>
            </div>
            <a 
              href={`https://portal.oiltank.app/register?reportId=${reportId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
            >
              Create Free Account →
            </a>
          </div>
        </div>
      )}

      <Toaster 
        position="bottom-center"
        toastOptions={{
          className: 'text-sm',
          duration: 2000,
          style: {
            background: '#363636',
            color: '#fff',
            marginBottom: '1rem'
          },
        }}
      />
    </div>
  );
};

export default ShareReport; 